<script setup lang="ts">
import { EToast, ETooltipProvider, provideUiStrings } from '@erpx/ui'
import { computed, watch } from 'vue'
import { RouterView, useRoute } from 'vue-router'

import AppShell from '@/components/AppShell.vue'
import StepUpDialog from '@/components/StepUpDialog.vue'
import { adoptUserLocale } from '@/i18n'
import { uiStringsBundle } from '@/i18n/uiStrings'
import { useAuthStore } from '@/stores/auth'

/**
 * Root. Auth screens render bare (no navigation to offer a visitor who has not
 * proved who they are); everything else renders inside the shell — and only
 * while somebody is signed in.
 *
 * That last condition is the session boundary's backstop for everything the
 * module-level resets cannot reach: component-local state. A dialog holding a
 * decrypted bank number, a half-typed form, a list's rows — none of it is in a
 * registry or the query cache; it lives in the component until the component
 * unmounts. Every way a session ends (sign-out, a refresh that fails, a forced
 * sign-out) clears `auth.user` synchronously, so gating the shell on
 * `isAuthenticated` unmounts the whole tree on the very next render, whether or
 * not the navigation to `/login` that follows ever completes (CLAUDE.md lesson
 * 11: a slow or cancelled navigation must not leave the old screen up).
 *
 * In between — a cold load whose session is still being restored, or the
 * moment after a session ends and before `/login` resolves — the root renders
 * an empty, busy surface in the app's own background colour. Never the shell:
 * on a cold load it would have no identity to show, and after a sign-out it
 * would be the previous person's.
 *
 * Two app-wide concerns land here because there is nowhere higher: the design
 * system's own strings are handed their translations (see `i18n/uiStrings.ts`),
 * and the signed-in user's `locale` from `/auth/me` becomes the app's locale —
 * a person whose profile says Dhivehi gets Dhivehi on any machine, not only the
 * one where they last picked it.
 *
 * The step-up dialog is mounted here for the same reason: a `403
 * ERR_STEP_UP_REQUIRED` can come back from any screen, so the thing that
 * answers it cannot live on one (§6, §13). It holds no session data of its own
 * — `clearSession()` resets the broker it renders from.
 */

const route = useRoute()
const auth = useAuthStore()

const bare = computed(() => route.meta.bare === true)

/**
 * A bare route renders outside the shell, but that does not exempt it from the
 * session gate. `change-password` is bare and NOT public — it is the forced
 * password change, a screen belonging to one signed-in person. If the bare
 * branch ignored auth, ending the session would leave that screen mounted
 * until the lazily-loaded `/login` chunk resolved (or forever, if that
 * navigation stalls or is cancelled — CLAUDE.md lesson 11.1). So a bare route
 * renders only when it is public (sign-in, forgot/reset password, invite) or
 * somebody is signed in; otherwise the same busy surface the shell gate uses.
 *
 * Cold load of `/change-password` does not flash: until the guard's bootstrap
 * resolves, the current route is still `START_LOCATION` (not bare), and once
 * it resolves to `change-password` the session is already restored.
 */
const bareAllowed = computed(() => route.meta.public === true || auth.isAuthenticated)

provideUiStrings(uiStringsBundle())

watch(() => auth.locale, adoptUserLocale, { immediate: true })
</script>

<template>
  <ETooltipProvider>
    <!-- A disallowed bare route implies signed out, so it falls through to the busy surface. -->
    <RouterView v-if="bare && bareAllowed" />
    <AppShell v-else-if="!bare && auth.isAuthenticated" />
    <div v-else class="app-pending" aria-busy="true" data-testid="app-pending" />
    <StepUpDialog />
    <EToast />
  </ETooltipProvider>
</template>

<style>
.app-pending {
  min-height: 100vh;
  background-color: var(--erpx-color-surface-app);
}
</style>
